import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";

import moment from "moment";
import { ISLADocument } from "src/Modules/Company/Interfaces/ISLAInfo";
import { getFormValidation } from "src/Shared/Components";
import GridFormControl from "src/Shared/Components/GridFormControl/GridFormControl";
import { EDITOR_TYPES } from "src/Shared/Enums";
import { AttachmentTypeEnum } from "src/Shared/Enums/AttachmentType.enum";
import { IGridFormControlProps } from "src/Shared/Interfaces/IGridFormControlProps.interface";
import { fields } from "./fileds";
import { useStyles } from "./UploadDocumentDialog.styles";

interface UploadDocumentDialogProps {
  open: boolean;
  uploadMode: "current" | "previous";
  onClose: () => void;
  onUpload: (document: ISLADocument) => void;
  expiryDate?: string;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const UploadDocumentDialog: React.FC<UploadDocumentDialogProps> = ({
  open,
  uploadMode,
  onClose,
  onUpload,
  expiryDate
}) => {
  const classes = useStyles();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState("");

  const documentForm = useMemo(() => getFormValidation(fields), []);
  const getDocField = (key: string) => documentForm.$(key);

  const uploadTitle =
    uploadMode === "current" ? "Upload SLA document" : "Upload previous SLA";

  const handleOpenFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;

    if (!file) {
      setSelectedFile(null);
      setFileError("");
      return;
    }

    const allowedExtensions = [".pdf", ".doc", ".docx"];
    const lowerName = file.name.toLowerCase();

    if (!allowedExtensions.some((ext) => lowerName.endsWith(ext))) {
      setSelectedFile(null);
      setFileError("Only PDF or DOC/DOCX files are allowed.");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setSelectedFile(null);
      setFileError("File size must be 10 MB or less.");
      return;
    }

    setSelectedFile(file);
    setFileError("");
  };

  const resetState = () => {
    setSelectedFile(null);
    setFileError("");
    documentForm.reset();

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleUpload = () => {
    let hasError = false;

    documentForm.validate();
    documentForm.showErrors();

    if (!selectedFile) {
      setFileError("Please select a file.");
      hasError = true;
    } else {
      setFileError("");
    }

    if (!documentForm.isValid) return;

    const expiryDate = getDocField("expiryDate")?.value;

    if (!expiryDate) {
      hasError = true;
    }

    if (hasError) return;

    const newDocument: ISLADocument = {
      slaAttachmentID: 0,
      expiryDate: moment.isMoment(expiryDate) ? expiryDate.format("DD/MM/YYYY") : expiryDate,
      isCurrent: uploadMode === "current",
      file: selectedFile,
      size: `${(selectedFile.size / 1024).toFixed(0)} KB`,
      attachmentID: 0,
      name: selectedFile.name,
      attachmentUrl: "",
      attachmentTypeID: AttachmentTypeEnum.SLAAttachment,
    };

    onUpload(newDocument);
    handleClose();
  };

  useEffect(() => {
    if (uploadMode === "current") {
      getDocField("expiryDate").set("rules", "required|validDate|minDateValidator");
    } else {
      getDocField("expiryDate").set("rules", "required|validDate");
    }
  }, [uploadMode]);

  const documentFormConfig: IGridFormControlProps = {
    row: 1,
    col: 1,
    spacing: 2,
    itemResponsive: {
      lg: 12,
      md: 12,
      sm: 12,
    },
    inputControls: [
      {
        field: getDocField("expiryDate"),
        fieldKey: "expiryDate",
        type: EDITOR_TYPES.DATE,
        minDate: uploadMode == "current" ? moment(new Date()) : null
      },
    ],
    form: documentForm,
  };

  useEffect(() => {
    if (open && expiryDate && uploadMode === "current") {
      getDocField("expiryDate")?.set(expiryDate);
    }
  }, [open, expiryDate]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: classes.uploadDialogPaper,
      }}
    >
      <DialogContent className={classes.uploadDialogContent}>
        <Box className={classes.uploadDialogHeader}>
          <Typography className={classes.uploadDialogTitle}>
            {uploadTitle}
          </Typography>

          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Typography className={classes.sectionLabel}>
          Document <span className={classes.requiredStar}>*</span>
        </Typography>

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        <Box className={classes.modalUploadBox} onClick={handleOpenFilePicker}>
          <CloudUploadOutlinedIcon className={classes.uploadIcon} />

          <Typography className={classes.uploadText}>
            {selectedFile ? <span className={classes.fileNameText}>{selectedFile.name}</span> : "Click to select file"}
            <span className={classes.uploadDot}> · </span>
            PDF or DOCX up to 10 MB
          </Typography>
        </Box>

        {!!fileError && (
          <Typography sx={{ mt: 1, color: "#d32f2f", fontSize: 13 }}>
            {fileError}
          </Typography>
        )}

        <Box sx={{ mt: 3 }}>
          <GridFormControl {...documentFormConfig} />
        </Box>

        <Box className={classes.uploadDialogActions}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>

          <Button variant="contained" onClick={handleUpload}>
            Upload
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UploadDocumentDialog;